/**
 * TK-Notice Web Component
 * Berechnet das TK-Gewicht im Warenkorb und zeigt Fortschritt zur Mindestbestellmenge
 */

if (!customElements.get('tk-notice')) {
  // Globale Flags um doppelte Event-Listener zu verhindern
  let tkNoticeInitialized = false;
  let tkNoticeInstance = null;

  class TKNotice extends HTMLElement {
    constructor() {
      super();
      this.tkTag = this.dataset.tkTag || 'Alle TK-Produkte';
      this.minimumWeight = parseFloat(this.dataset.minimumWeight) || 10;
      this.noticeText = this.dataset.noticeText || 'Dir fehlen noch {{ missing }} kg TK-Ware.';
      this.successText = this.dataset.successText || 'TK-Mindestbestellmenge erreicht!';
      this.collectionUrl = this.dataset.collectionUrl || '/collections/alle-produkte-a-tiefgefrorenes';
      this.buttonText = this.dataset.buttonText || 'Weitere TK-Produkte ansehen';
      this.observer = null;
    }

    connectedCallback() {
      this.progressBar = this.querySelector('[data-tk-progress]');
      this.progressFill = this.querySelector('[data-tk-progress-fill]');
      this.messageEl = this.querySelector('[data-tk-message]');
      this.weightEl = this.querySelector('[data-tk-weight]');
      this.buttonContainer = this.querySelector('[data-tk-button]');

      // Initial blocked status aus data-complete lesen
      this.isBlocked = this.dataset.complete !== 'true';

      // Prüfe ob wir auf der Cart-Page sind (keine sichtbare Notice, nur für Event-Handler)
      this.isCartPage = this.style.display === 'none';

      // Globale Event-Listener nur einmal registrieren
      if (!tkNoticeInitialized) {
        tkNoticeInitialized = true;
        this.setupGlobalEventListeners();
      }

      // Aktuelle Instanz speichern
      tkNoticeInstance = this;

      // MutationObserver nur im Drawer, nicht auf Cart-Page
      if (!this.isCartPage) {
        this.observeCartChanges();
      }

      // Initial Update nur im Drawer durchführen
      if (!this.isCartPage && !this.isBlocked) {
        this.calculateAndUpdate();
      }
    }

    disconnectedCallback() {
      // MutationObserver aufräumen
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    setupGlobalEventListeners() {
      // Event-Listener für Cart-Updates (mit Delay um nach AJAX-Rerender zu warten)
      document.addEventListener('cart-updated', () => {
        setTimeout(() => {
          // Prüfe ob noch TK-Produkte im DOM sind
          const tkItems = document.querySelectorAll('[data-is-tk="true"]');
          if (tkItems.length === 0) {
            // Keine TK-Produkte mehr - Message verstecken
            const blockedMessage = document.querySelector('[data-tk-blocked-message]');
            if (blockedMessage) {
              blockedMessage.style.display = 'none';
            }
            if (tkNoticeInstance) {
              tkNoticeInstance.isBlocked = false;
            }
          } else if (tkNoticeInstance && !tkNoticeInstance.isCartPage) {
            tkNoticeInstance.calculateAndUpdate();
          }
        }, 100);
      });

      // Pickup-Status ändert sich → TK-Blockierung neu bewerten
      document.addEventListener('pickup:change', () => {
        if (!tkNoticeInstance) return;
        if (tkNoticeInstance.isCartPage) {
          // Auf Cart-Page: direkt Button/Message synchen
          tkNoticeInstance.syncPickupState();
        } else {
          tkNoticeInstance.calculateAndUpdate();
        }
      });

      // Event Delegation auf Document-Level (überlebt AJAX Re-Renders)
      document.addEventListener('click', (e) => {
        // Bypass-Button für lokale Abholung
        if (e.target.closest('[data-tk-bypass-button]')) {
          e.preventDefault();
          if (tkNoticeInstance) tkNoticeInstance.bypassBlocking();
          return;
        }

        // Produkt entfernen - nach kurzer Zeit prüfen ob noch TK-Produkte da sind
        if (e.target.closest('.remove, [href*="quantity=0"]')) {
          setTimeout(() => {
            const blockedMessage = document.querySelector('[data-tk-blocked-message]');
            if (blockedMessage) {
              blockedMessage.style.display = 'none';
            }
            // Nach AJAX-Update nochmal prüfen
            setTimeout(() => {
              if (tkNoticeInstance && !tkNoticeInstance.isCartPage) {
                tkNoticeInstance.calculateAndUpdate();
              }
            }, 500);
          }, 100);
        }

        const target = e.target.closest('#CheckOut');
        if (target) {
          // Prüfe ob der Checkout-Button den Typ "button" hat (blockiert)
          const checkoutBtn = document.getElementById('CheckOut');
          const isButtonType = checkoutBtn && checkoutBtn.type === 'button';

          // Prüfe ob noch TK-Produkte im DOM sind
          const tkItems = document.querySelectorAll('[data-is-tk="true"]');
          const hasTKInDOM = tkItems.length > 0;

          // Nur blockieren wenn Button-Typ "button" ist UND entweder TK im DOM oder auf Cart-Page mit data-tk-blocked
          const shouldBlock = isButtonType && (hasTKInDOM || (checkoutBtn && checkoutBtn.dataset.tkBlocked === 'true'));

          if (!shouldBlock) {
            // Nicht blockieren
            if (tkNoticeInstance) tkNoticeInstance.isBlocked = false;
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (tkNoticeInstance) tkNoticeInstance.showBlockedMessage();
          return false;
        }
      }, true);

      // Form submit auch abfangen
      document.addEventListener('submit', (e) => {
        if (e.target.id === 'cart' && tkNoticeInstance && tkNoticeInstance.isBlocked) {
          e.preventDefault();
          e.stopPropagation();
          tkNoticeInstance.showBlockedMessage();
        }
      }, true);
    }

    observeCartChanges() {
      // Alten Observer aufräumen falls vorhanden
      if (this.observer) {
        this.observer.disconnect();
      }

      // MutationObserver auf Subtotal-Container um nach AJAX-Rerender zu aktualisieren
      const subtotalContainer = document.getElementById('AjaxCartSubtotal');
      if (subtotalContainer) {
        this.observer = new MutationObserver(() => {
          // Nach DOM-Änderung den Button-Status korrigieren
          setTimeout(() => this.calculateAndUpdate(), 50);
        });
        this.observer.observe(subtotalContainer, { childList: true, subtree: true });
      }
    }

    async calculateAndUpdate() {
      try {
        // Message sofort verstecken bei jedem Cart-Update
        const blockedMessage = document.querySelector('[data-tk-blocked-message]');
        if (blockedMessage) {
          blockedMessage.style.display = 'none';
        }

        const cart = await this.fetchCart();
        const tkWeight = this.calculateTKWeight(cart);
        this.updateUI(tkWeight);
      } catch (error) {
        console.error('TK-Notice: Fehler beim Berechnen', error);
      }
    }

    async fetchCart() {
      const response = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      return response.json();
    }

    calculateTKWeight(cart) {
      let totalTKWeight = 0;
      let hasTKProducts = false;

      // Primäre Methode: Lese TK-Info aus DOM data-Attributen
      const lineItems = document.querySelectorAll('[data-js-cart-item]');

      if (lineItems.length > 0) {
        lineItems.forEach(lineItem => {
          const isTK = lineItem.dataset.isTk === 'true';
          if (isTK) {
            hasTKProducts = true;
            const weightGrams = parseInt(lineItem.dataset.weightGrams, 10) || 0;
            totalTKWeight += weightGrams / 1000;
          }
        });
      } else {
        // Fallback: Nutze cart.js Daten mit Tag-Prüfung über DOM
        // Hinweis: item.grams in cart.js ist Gewicht pro Einheit
        cart.items.forEach(item => {
          const isTK = this.checkIfTKProduct(item);
          if (isTK) {
            hasTKProducts = true;
            const weightInKg = (item.grams * item.quantity) / 1000;
            totalTKWeight += weightInKg;
          }
        });
      }

      return {
        weight: totalTKWeight,
        hasTKProducts: hasTKProducts
      };
    }

    checkIfTKProduct(item) {
      // Prüfe Tags über data-Attribute am Line-Item (primäre Methode)
      const lineItemEl = document.querySelector(`[data-line-item-key="${item.key}"]`);
      if (lineItemEl) {
        // Direkte TK-Flag prüfen
        if (lineItemEl.dataset.isTk === 'true') {
          return true;
        }
        // Fallback: Tags prüfen
        const tags = lineItemEl.dataset.productTags || '';
        if (tags.includes(this.tkTag)) {
          return true;
        }
      }

      // Fallback-Prüfungen für cart.js Daten
      if (item.properties && item.properties._tk_product === 'true') {
        return true;
      }

      if (item.product_type && item.product_type.toLowerCase().includes('tk')) {
        return true;
      }

      const handle = (item.handle || '').toLowerCase();
      const title = (item.title || '').toLowerCase();
      if (handle.includes('tk-') || handle.includes('frost') ||
          title.includes('tk-') || title.includes('frost')) {
        return true;
      }

      return false;
    }

    updateUI(tkData) {
      const { weight, hasTKProducts } = tkData;

      // Wenn keine TK-Produkte, verstecke die Notice und Message
      if (!hasTKProducts) {
        this.style.display = 'none';
        this.isBlocked = false;
        const blockedMessage = document.querySelector('[data-tk-blocked-message]');
        if (blockedMessage) {
          blockedMessage.style.display = 'none';
        }
        return;
      }

      this.style.display = '';

      const percentage = Math.min((weight / this.minimumWeight) * 100, 100);
      const missingWeight = Math.max(this.minimumWeight - weight, 0);
      const isComplete = weight >= this.minimumWeight;

      // Update Progress Bar
      if (this.progressFill) {
        this.progressFill.style.width = `${percentage}%`;
        this.progressFill.classList.toggle('tk-notice__progress-fill--complete', isComplete);
      }

      // Update Gewichtsanzeige (deutsches Format, kompakt)
      if (this.weightEl) {
        this.weightEl.textContent = `${this.formatDE(weight)} / ${this.formatDE(this.minimumWeight)} kg`;
      }

      // Update Message
      if (this.messageEl) {
        if (isComplete) {
          this.messageEl.textContent = this.successText;
          this.messageEl.classList.add('tk-notice__message--success');
          this.messageEl.classList.remove('tk-notice__message--warning');
        } else {
          const message = this.noticeText
            .replace('{{ missing }}', this.formatDE(missingWeight))
            .replace('{{ current }}', this.formatDE(weight));
          this.messageEl.textContent = message;
          this.messageEl.classList.remove('tk-notice__message--success');
          this.messageEl.classList.add('tk-notice__message--warning');
        }
      }

      // Update Button visibility
      if (this.buttonContainer) {
        this.buttonContainer.style.display = isComplete ? 'none' : '';
      }

      // Update data attributes für CSS
      this.dataset.complete = isComplete;
      this.dataset.percentage = Math.round(percentage);

      // Update Checkout-Blockierung (Pickup hebt Blockierung auf)
      this.updateCheckoutBlocking(!isComplete && hasTKProducts && !this.isPickupActive());
    }

    isPickupActive() {
      const picker = document.querySelector('pickup-date-picker');
      if (!picker) return false;
      const toggle = picker.querySelector('[data-pickup-toggle]');
      const dateInput = picker.querySelector('[data-pickup-date]');
      const timeInput = picker.querySelector('[data-pickup-time]');
      return !!(toggle && toggle.checked && dateInput && dateInput.value && timeInput && timeInput.value);
    }

    syncPickupState() {
      // Auf Cart-Page: Button-Status und Message anhand Pickup/TK-Gewicht neu setzen
      const checkoutBtn = document.getElementById('CheckOut');
      if (!checkoutBtn) return;
      const belowMinimum = checkoutBtn.dataset.tkBelowMinimum === 'true';
      const shouldBlock = belowMinimum && !this.isPickupActive();
      this.updateCheckoutBlocking(shouldBlock);
    }

    updateCheckoutBlocking(blocked) {
      const checkoutBtn = document.getElementById('CheckOut');
      const viewCartBtn = document.getElementById('ViewCart');
      const blockedMessage = document.querySelector('[data-tk-blocked-message]');

      // Speichere blocked-Status für Click-Handler
      this.isBlocked = blocked;

      if (checkoutBtn) {
        // Button-Typ ändern um Form-Submit zu verhindern, aber NICHT ausgrauen
        checkoutBtn.type = blocked ? 'button' : 'submit';
        if (blocked) {
          checkoutBtn.dataset.tkBlocked = 'true';
        } else {
          delete checkoutBtn.dataset.tkBlocked;
        }
      }

      if (viewCartBtn) {
        if (blocked) {
          viewCartBtn.dataset.tkBlocked = 'true';
        } else {
          delete viewCartBtn.dataset.tkBlocked;
        }
      }

      // Message verstecken wenn nicht mehr blockiert (Mindestmenge erreicht oder keine TK-Produkte)
      if (blockedMessage && !blocked) {
        blockedMessage.style.display = 'none';
      }
    }

    showBlockedMessage() {
      const blockedMessage = document.querySelector('[data-tk-blocked-message]');
      if (blockedMessage) {
        blockedMessage.style.display = 'block';
      }
    }

    bypassBlocking() {
      // Blockierung temporär aufheben für lokale Abholung
      this.isBlocked = false;

      const checkoutBtn = document.getElementById('CheckOut');
      const viewCartBtn = document.getElementById('ViewCart');
      const blockedMessage = document.querySelector('[data-tk-blocked-message]');

      if (checkoutBtn) {
        checkoutBtn.classList.remove('button--disabled');
        checkoutBtn.type = 'submit';
        delete checkoutBtn.dataset.tkBlocked;
      }

      if (viewCartBtn) {
        viewCartBtn.classList.remove('button--disabled');
        delete viewCartBtn.dataset.tkBlocked;
      }

      if (blockedMessage) {
        blockedMessage.style.display = 'none';
      }

      // Automatisch zum Checkout weiterleiten
      if (checkoutBtn) {
        checkoutBtn.click();
      }
    }

    // Formatiert Zahl ins deutsche Format (Komma statt Punkt)
    formatDE(num) {
      return num.toFixed(2).replace('.', ',');
    }
  }

  customElements.define('tk-notice', TKNotice);
}
